import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { useUser } from '@application/authentication/use_user';
import { withTranslation } from '@application/i18n/with_translation';
import { validateRequired } from '@application/validators';
import {
	BadgeText,
	ButtonArrange,
	CellulaFormSubmitData,
	Divider,
	Flex,
	Form,
	Icon,
	InputTextArea,
	Label,
	Sheet,
	Text,
	TextLink,
} from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { DataList } from '@component/data_list.component';
import { IframeSheet } from '@component/iframe_sheet.component';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { FormCard } from '@feature/dynamic_form/form_card.component';
import { FileUpload } from '@feature/file_manager/file_upload.component';
import { FileManagerService, FileManagerServiceReadyState } from '@feature/file_manager/mod';
import { Payment } from '@feature/payment/payment.component';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { useResolve } from '@visiba/instantiation';
import { useState } from 'react';
import styled from 'styled-components';
import { Skeleton } from '../../components/skeleton';
import { useBasket } from '../../hooks/use-basket';
import { type CreateNewMessageFlowData, useCreateNewMessage } from '../../hooks/use-create-new-message';
import type { CreateNewMessageSettings } from '../../hooks/use-create-new-message-settings';
import { ParticipantsForm } from '../access/participants_form.component';
import { useSaveParticipants } from '../access/use-save-participants';
import { NonRecoverableError, useScreenConclusion } from './use-screen-conclusion';

function getParticipantName(participant: NonNullable<ReturnType<typeof useBasket>['basket']>['Participants'][number]) {
	return [participant.GivenName, participant.Surname].filter(Boolean).join(' ') || withTranslation('PW.Common.Anonymous');
}

const StyledBottomSection = styled(Flex)`
	text-align: center;
	font: ${tokens.text.label.smallRegular};

	${tokens.breakpointQuery.small} {
		text-align: left;
	}
`;

const REASON_MAX_LENGTH = 4000;

export const ScreenConclusion = () => {
	const { flowSettings, flowData } = useCreateNewMessage();

	if (flowSettings.flowState !== 'accessible' || flowData.flowState !== 'accessible') {
		return <Skeleton />;
	}

	return <Inner flowSettings={flowSettings.value} flowData={flowData.value} />;
};

interface InnerProps {
	flowSettings: CreateNewMessageSettings;
	flowData: CreateNewMessageFlowData;
}

function Inner({ flowSettings, flowData }: InnerProps) {
	const { goToScreen } = useCreateNewMessage();
	const { termsOfServiceUrl, submit, waitForCommitStatus } = useScreenConclusion();
	const { reasonForVisitId, formManager, basket } = useBasket();
	const user = useUser();
	const { currentlyRepresentedPatient } = useRepresentablePatients();
	const fileManagerService = useResolve(FileManagerService);
	const [payment, setPayment] = useState<{ paymentBillId: number; paymentAmount: string; commitJobId: number } | null>(null);
	const [disableSubmit, setDisableSubmit] = useState<string | false>(false);
	const [isParticipantsSheetOpen, setIsParticipantsSheetOpen] = useState(false);
	const analytic = useAnalyticContext();

	const selectedRfv = flowData.reasonForVisits.find((rfv) => rfv.Id === reasonForVisitId) ?? null;
	const participants = basket?.Participants ?? [];
	// The Patient participant is the subject of care. When representing someone else it is a
	// distinct person; when the user is the patient themselves it is "me" and is already shown by
	// the read-only "me" row below, so it must not be listed a second time.
	const subjectParticipants =
		currentlyRepresentedPatient != null ? participants.filter((participant) => participant.Type === 'Patient') : [];
	const otherParticipants = participants.filter((participant) => participant.Type !== 'Patient');

	function handleFileManagerStateChange(state: FileManagerServiceReadyState) {
		setDisableSubmit(
			state === FileManagerServiceReadyState.NotReady
				? withTranslation('View.CreateNewMessage.ScreenConclusion.AttachedFilesNotReady')
				: false,
		);
	}

	async function handleSubmit(data: CellulaFormSubmitData) {
		analytic.trackEvent('Clicked', 'Submit button');

		const submitResult = await submit({
			...data,
			files: fileManagerService.peakUploadedWorkers(),
		});

		if (submitResult.err) {
			if (submitResult.err instanceof NonRecoverableError) {
				goToScreen('error');

				return;
			}

			throw submitResult.err;
		}

		if (submitResult.ok?.commitJobId && submitResult.ok?.paymentBillId && flowData?.price) {
			setPayment({
				paymentBillId: submitResult.ok.paymentBillId,
				paymentAmount: flowData.price,
				commitJobId: submitResult.ok.commitJobId,
			});
		} else {
			goToScreen('done', {
				conversationId: submitResult.ok?.messageConversationId ?? undefined,
			});
		}
	}

	async function handlePaymentSuccess() {
		if (!payment?.commitJobId) {
			goToScreen('error');

			return;
		}

		const paymentResult = await waitForCommitStatus(payment.commitJobId);
		if (paymentResult.err) {
			goToScreen('error');

			return;
		}

		goToScreen('done', {
			conversationId: paymentResult.ok?.messageConversationId ?? undefined,
		});
	}

	function handlePaymentAbort() {
		setPayment(null);
	}

	const showMessageTextArea = !flowSettings.predefinedDescriptionOfMatter;
	const showAttachFiles = flowSettings.attachFiles;
	const showDetails = flowData.price || selectedRfv || formManager.getForms().length;
	const emptyDescription =
		showMessageTextArea || showAttachFiles || showDetails
			? undefined
			: withTranslation('View.CreateNewMessage.ScreenConclusion.EmptyDescription');

	return (
		<>
			<TopBar.Content goBack />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Title
					title={withTranslation('View.CreateNewMessage.ScreenConclusion.Title')}
					titleSize='small'
					description={emptyDescription}
				/>

				<Flex grow='1' gap={tokens.size.x3}>
					<Form.Root onSubmit={handleSubmit} options={{}}>
						{showMessageTextArea || showAttachFiles ? (
							<Flex gap={tokens.size.x2}>
								{showMessageTextArea ? (
									<div>
										<InputTextArea
											name='message'
											label={withTranslation('View.CreateNewMessage.ScreenConclusion.Input.Label.Reason')}
											minHeight={120}
											maxHeight={400}
											autoHeight
											required
											characterCount={REASON_MAX_LENGTH}
											options={{
												validate: {
													required: (v) =>
														validateRequired(v) ||
														withTranslation('View.CreateNewMessage.ScreenConclusion.ValidationError.Required.Reason'),
												},
												maxLength: {
													value: REASON_MAX_LENGTH,
													message: withTranslation('View.CreateNewMessage.ScreenConclusion.ValidationError.MaxLength.Reason', {
														maxLength: `${REASON_MAX_LENGTH}`,
													}),
												},
											}}
										/>
									</div>
								) : null}

								{showAttachFiles ? (
									<Flex gap={tokens.size.x1}>
										<fieldset style={{ display: 'contents' }}>
											<Label type='legend' optional>
												{withTranslation('Form.Input.Label.AttachedFiles')}
											</Label>
											<FileUpload.Root identityEndpointPath='client/v2/userfiles' onStateChange={handleFileManagerStateChange}>
												<FileUpload.List />
												<FileUpload.DropZone>
													<FileUpload.Button />
												</FileUpload.DropZone>
											</FileUpload.Root>
										</fieldset>
									</Flex>
								) : null}
							</Flex>
						) : null}

						{showDetails ? (
							<DataList.Root>
								{flowData.price ? (
									<DataList.Item label={withTranslation('Common.Label.Cost')}>
										<BadgeText variant='info'>{flowData.price}</BadgeText>
									</DataList.Item>
								) : null}
								{selectedRfv ? (
									<DataList.Item label={withTranslation('Common.Label.Rfv')}>{selectedRfv?.Name ?? ''}</DataList.Item>
								) : null}
								{formManager.getForms().length > 0 ? (
									<DataList.Item label={withTranslation('Common.Label.AnsweredForms')} direction='vertical'>
										<Flex gap={tokens.spacing.x1}>
											{formManager.getForms().map((form) => (
												<FormCard key={form.formId} formWorker={form} analyticAction='' onCompleted={false} />
											))}
										</Flex>
									</DataList.Item>
								) : null}
							</DataList.Root>
						) : null}

						{participants.length > 0 ? (
							<>
								<Divider />
								<Flex gap={tokens.spacing.x1}>
									<Flex direction='row' justify='space-between' align='center'>
										<Text variant='labelSemiBold' color={tokens.color.content.default}>
											{withTranslation('Common.Label.Participants')}
										</Text>
										<TextLink
											as='button'
											type='button'
											size='small'
											onClick={() => {
												analytic.trackEvent('Clicked', 'Change participants');
												setIsParticipantsSheetOpen(true);
											}}
											style={{ color: tokens.color.content.interactive }}
										>
											{withTranslation('View.CreateNewMessage.ScreenConclusion.ChangeParticipants')}
										</TextLink>
									</Flex>
									<Flex gap={tokens.spacing.x0_5}>
										<Flex direction='row' align='center' gap={tokens.spacing.x1}>
											<Icon type='checkCircleFilled' size='small' color={tokens.color.success.bgActive} />
											<Text variant='labelSemiBold' color={tokens.color.content.default}>
												{user?.commonName
													? withTranslation('View.Booking.ScreenAccess.Option.Me', { name: user.commonName })
													: withTranslation('View.Booking.ScreenAccess.Option.MeShort')}
											</Text>
										</Flex>
										{subjectParticipants.map((participant) => (
											<Flex key={participant.Id} direction='row' align='center' gap={tokens.spacing.x1}>
												<Icon type='checkCircleFilled' size='small' color={tokens.color.success.bgActive} />
												<Text variant='labelSemiBold' color={tokens.color.content.default}>
													{getParticipantName(participant)}
												</Text>
											</Flex>
										))}
										{otherParticipants.map((participant) => (
											<Flex key={participant.Id} direction='row' align='center' gap={tokens.spacing.x1}>
												<Icon
													type={participant.Selected ? 'checkCircleFilled' : 'prohibited'}
													size='small'
													color={participant.Selected ? tokens.color.success.bgActive : tokens.color.content.default}
												/>
												<Text variant={participant.Selected ? 'labelSemiBold' : 'labelRegular'} color={tokens.color.content.default}>
													{getParticipantName(participant)}
												</Text>
											</Flex>
										))}
									</Flex>
								</Flex>
							</>
						) : null}

						<Form.Error />

						<StyledBottomSection grow='1' justify='flex-end' gap={tokens.spacing.x2}>
							<div>
								{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part1')}{' '}
								<IframeSheet
									src={termsOfServiceUrl ?? ''}
									trigger={
										<TextLink as='button' size='small'>
											{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Link')}
										</TextLink>
									}
								/>{' '}
								{withTranslation('View.CreateNewMessage.ScreenConclusion.Terms.Part2')}
							</div>
							<ButtonArrange>
								<Form.SubmitButton disabled={!!disableSubmit} disabledText={disableSubmit || ''}>
									{flowData.requirePayment
										? withTranslation('View.CreateNewMessage.ScreenConclusion.ButtonWithPayment')
										: withTranslation('View.CreateNewMessage.ScreenConclusion.ButtonWithoutPayment')}
								</Form.SubmitButton>
							</ButtonArrange>
						</StyledBottomSection>
					</Form.Root>
				</Flex>
			</Flex>
			{payment ? (
				<PaymentSheet
					billId={payment.paymentBillId}
					paymentAmount={payment.paymentAmount}
					onSuccess={handlePaymentSuccess}
					onAbort={handlePaymentAbort}
				/>
			) : null}
			{isParticipantsSheetOpen ? <ChangeParticipantsSheet onClose={() => setIsParticipantsSheetOpen(false)} /> : null}
		</>
	);
}

interface ChangeParticipantsSheetProps {
	onClose: () => void;
}

const StyledChangeParticipantsInner = styled.div`
	width: 100%;
	max-width: 600px;
	margin: 0 auto;
	padding: 0 ${tokens.spacing.x2};
	box-sizing: border-box;
`;

function ChangeParticipantsSheet({ onClose }: ChangeParticipantsSheetProps) {
	const { basket } = useBasket();
	const { access, save } = useSaveParticipants();
	const participants = basket?.Participants ?? [];

	async function handleSubmit(data: CellulaFormSubmitData) {
		await save({ access: data.access });
		onClose();
	}

	return (
		<Sheet
			defaultOpen
			onClose={onClose}
			variant='half'
			label={withTranslation('View.CreateNewMessage.ScreenConclusion.ChangeParticipantsSheet.Label')}
		>
			<StyledChangeParticipantsInner>
				<Flex gap={tokens.spacing.x2}>
					<Title title={withTranslation('View.CreateNewMessage.ScreenConclusion.ChangeParticipantsSheet.Title')} titleSize='small' />
					<ParticipantsForm
						participants={participants}
						defaultAccess={access}
						submitLabel={withTranslation('PW.Common.Next')}
						inputLabel={withTranslation('View.CreateNewMessage.ScreenConclusion.ChangeParticipantsSheet.Description')}
						onSubmit={handleSubmit}
					/>
				</Flex>
			</StyledChangeParticipantsInner>
		</Sheet>
	);
}

interface Props {
	billId: number;
	paymentAmount: string;
	onSuccess?: () => void;
	onAbort?: () => void;
}

function PaymentSheet({ paymentAmount, billId, onSuccess, onAbort }: Props): JSX.Element {
	return (
		<Sheet onClose={onAbort} defaultOpen variant='full-restricted'>
			<Payment billId={billId} paymentAmount={paymentAmount} onSuccess={onSuccess} onAbort={onAbort} asDialogContent />
		</Sheet>
	);
}
